import { createRoute, Link } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Mono,
  PageHeader,
  ResourceTable,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '@verydia/ui';
import type { OrganizationResource, PlatformUserResource } from '@verydia/contracts';
import { Building2, Plus, UserPlus, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { rootRoute } from './root.tsx';
import { describeFailure, useMe } from '../lib/api.ts';
import { useAuth } from '../lib/auth/index.ts';
import {
  useCreateOrganization,
  useSeedOrganizationOwner,
  useGrantPlatformRole,
  useOrganizations,
  usePlatformUsers,
  useSetPlatformUserStatus,
} from '../lib/identity-queries.ts';

const ORGANIZATION_TONE = { ACTIVE: 'success', SUSPENDED: 'warning', ARCHIVED: 'neutral' } as const;
const USER_TONE = { ACTIVE: 'success', SUSPENDED: 'warning', DISABLED: 'neutral' } as const;

function formatDate(value: string | null): string {
  if (value === null) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Mirrors `slugFromDisplayName` on the server, so the preview is the real thing. */
function previewSlug(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/**
 * Platform administration is not a place a non-operator should discover by
 * guessing a URL. The API refuses either way; this stops the page rendering an
 * empty table and a "create" button that will always fail.
 */
function useIsPlatformOperator(): { ready: boolean; allowed: boolean; roles: readonly string[] } {
  const auth = useAuth();
  const me = useMe(auth.status === 'authenticated');
  const roles = me.data?.data.platform_roles ?? [];
  return { ready: me.isSuccess, allowed: roles.length > 0, roles };
}

function NotAnOperator() {
  return (
    <SectionCard flush>
      <EmptyState
        icon={<Building2 />}
        title="Platform administration"
        description="This area is for Parallax Motion operators. Your account does not hold a platform role."
      />
    </SectionCard>
  );
}

/* --------------------------- create organization -------------------------- */

function CreateOrganizationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const create = useCreateOrganization();
  const failure = create.isError ? describeFailure(create.error) : null;

  const close = () => {
    create.reset();
    setDisplayName('');
    setSlug('');
    onOpenChange(false);
  };

  const effectiveSlug = slug.trim().length > 0 ? slug.trim() : previewSlug(displayName);

  const submit = () => {
    if (displayName.trim().length === 0) return;
    create.mutate(
      {
        display_name: displayName.trim(),
        ...(slug.trim().length > 0 ? { slug: slug.trim() } : {}),
      },
      { onSuccess: close },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Create a vendor organization"
      description="Creating an organization is Parallax Motion's act. There is no self-serve signup."
      footer={
        <>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus />}
            disabled={create.isPending || displayName.trim().length === 0}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field
          label="Display name"
          required
          description="The name people see. It can be changed later."
          error={failure?.message}
        >
          {(props) => (
            <TextInput
              {...props}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Slug"
          description={
            slug.trim().length > 0
              ? 'Used in URLs and API paths. Permanent once set.'
              : 'Left blank, it is derived from the display name. Permanent once set.'
          }
        >
          {(props) => (
            <TextInput
              {...props}
              placeholder={previewSlug(displayName) || 'auto-generated'}
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
            />
          )}
        </Field>

        {effectiveSlug.length > 0 ? (
          <p className="text-[12px] text-[var(--vc-text-subtle)]">
            Will be created as <Mono>{effectiveSlug}</Mono>
          </p>
        ) : null}

        {failure?.requestId ? (
          <p className="text-[11.5px] text-[var(--vc-text-subtle)]">
            Request <Mono>{failure.requestId}</Mono>
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/* ----------------------------- seed first owner --------------------------- */

function SeedOwnerDialog({
  organization,
  onOpenChange,
}: {
  organization: OrganizationResource;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const seed = useSeedOrganizationOwner();
  const failure = seed.isError ? describeFailure(seed.error) : null;
  const result = seed.data?.data;

  const close = () => {
    seed.reset();
    setEmail('');
    onOpenChange(false);
  };

  const submit = () => {
    if (email.trim().length === 0) return;
    seed.mutate({ organizationId: organization.id, email: email.trim() });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Give ${organization.display_name} its first owner`}
      description="A new organization has no members, so nobody inside it can invite anyone. This is the single hand-over that starts it."
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={seed.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<UserPlus />}
              disabled={seed.isPending || email.trim().length === 0}
              onClick={submit}
            >
              {seed.isPending ? 'Working…' : 'Make owner'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <p className="text-[13px] text-[var(--vc-text)]" role="status">
          {result.outcome === 'membership_created'
            ? `${result.email} is now the owner of ${organization.display_name}.`
            : `${result.email} has been invited as owner. They become a member when they first sign in.`}
        </p>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field
            label="Owner's email address"
            required
            description="If they already have a Verydia account this takes effect immediately. If not, they are invited and join at first sign-in."
            error={failure?.message}
          >
            {(props) => (
              <TextInput
                {...props}
                type="email"
                autoComplete="off"
                placeholder="owner@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          {failure?.requestId ? (
            <p className="text-[11.5px] text-[var(--vc-text-subtle)]">
              Request <Mono>{failure.requestId}</Mono>
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}

/* -------------------------- organizations (list) -------------------------- */

function PlatformOrganizationsPage() {
  const operator = useIsPlatformOperator();
  const organizations = useOrganizations(operator.allowed);
  const [createOpen, setCreateOpen] = useState(false);
  const [seedingOwnerFor, setSeedingOwnerFor] = useState<OrganizationResource | null>(null);

  const columns: ReadonlyArray<Column<OrganizationResource>> = [
    {
      key: 'name',
      header: 'Organization',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-[var(--vc-text)]">{row.display_name}</span>
          <Mono className="text-[12px] text-[var(--vc-text-subtle)]">{row.slug}</Mono>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-32',
      render: (row) => <StatusBadge tone={ORGANIZATION_TONE[row.status]}>{row.status}</StatusBadge>,
    },
    {
      key: 'created',
      header: 'Created',
      width: 'w-36',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'id',
      header: 'Identifier',
      width: 'w-64',
      render: (row) => <Mono className="text-[12px]">{row.id}</Mono>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: 'w-36',
      render: (row) => (
        <Button size="sm" onClick={() => setSeedingOwnerFor(row)}>
          Set owner
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Organizations"
        description="Every vendor organization on the platform."
        breadcrumbs={[{ label: 'Platform', href: '/' }, { label: 'Organizations' }]}
        linkComponent={Link}
        actions={
          operator.allowed ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
              Create organization
            </Button>
          ) : null
        }
      />

      {!operator.ready ? (
        <SectionCard flush>
          <LoadingState label="Checking your access" />
        </SectionCard>
      ) : !operator.allowed ? (
        <NotAnOperator />
      ) : (
        <SectionCard flush>
          {organizations.isPending ? (
            <LoadingState label="Loading organizations" />
          ) : organizations.isError ? (
            <ErrorState
              description={describeFailure(organizations.error).message}
              requestId={describeFailure(organizations.error).requestId}
              onRetry={() => void organizations.refetch()}
            />
          ) : (
            <ResourceTable
              caption="Vendor organizations"
              columns={columns}
              rows={organizations.data.data.data}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  icon={<Building2 />}
                  title="No organizations yet"
                  description="Create the first vendor organization to begin onboarding."
                />
              }
            />
          )}
        </SectionCard>
      )}

      <CreateOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} />

      {seedingOwnerFor !== null ? (
        <SeedOwnerDialog
          organization={seedingOwnerFor}
          onOpenChange={(open) => {
            if (!open) setSeedingOwnerFor(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* ----------------------------- people (list) ------------------------------ */

function PlatformUsersPage() {
  const operator = useIsPlatformOperator();
  const users = usePlatformUsers(operator.allowed);
  const setStatus = useSetPlatformUserStatus();
  const grant = useGrantPlatformRole();
  const isOwner = operator.roles.includes('PLATFORM_OWNER');

  const [confirming, setConfirming] = useState<{
    user: PlatformUserResource;
    intent: 'suspend' | 'reactivate';
  } | null>(null);

  const columns: ReadonlyArray<Column<PlatformUserResource>> = [
    {
      key: 'person',
      header: 'Person',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-[var(--vc-text)]">{row.display_name}</span>
          <span className="text-[12px] text-[var(--vc-text-subtle)]">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'platform-role',
      header: 'Platform role',
      width: 'w-48',
      render: (row) => {
        if (!isOwner) {
          return (
            <span className="text-[var(--vc-text-muted)]">
              {row.platform_roles.length === 0 ? '—' : row.platform_roles.join(', ')}
            </span>
          );
        }
        return (
          <Select
            aria-label={`Platform role for ${row.display_name}`}
            value={row.platform_roles[0] ?? ''}
            disabled={grant.isPending}
            onChange={(event) => {
              if (event.target.value === '') return;
              grant.mutate({ userId: row.id, role: event.target.value });
            }}
          >
            <option value="">None</option>
            <option value="PLATFORM_ADMIN">Platform admin</option>
            <option value="PLATFORM_OWNER">Platform owner</option>
          </Select>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-32',
      render: (row) => <StatusBadge tone={USER_TONE[row.status]}>{row.status}</StatusBadge>,
    },
    {
      key: 'last-seen',
      header: 'Last seen',
      width: 'w-32',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{formatDate(row.last_seen_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: 'w-32',
      render: (row) => (
        <Button
          size="sm"
          variant={row.status === 'SUSPENDED' ? 'secondary' : 'danger'}
          onClick={() =>
            setConfirming({
              user: row,
              intent: row.status === 'SUSPENDED' ? 'reactivate' : 'suspend',
            })
          }
        >
          {row.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
        </Button>
      ),
    },
  ];

  const failure = setStatus.isError ? describeFailure(setStatus.error) : null;
  const grantFailure = grant.isError ? describeFailure(grant.error) : null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="People"
        description="Every named user known to Verydia Control."
        breadcrumbs={[{ label: 'Platform', href: '/' }, { label: 'People' }]}
        linkComponent={Link}
      />

      {grantFailure ? (
        <div
          role="alert"
          className="rounded-[var(--vc-radius-md)] border border-[var(--vc-danger-text)] bg-[var(--vc-surface)] px-4 py-3 text-[13px] text-[var(--vc-danger-text)]"
        >
          {grantFailure.message}
        </div>
      ) : null}

      {!operator.ready ? (
        <SectionCard flush>
          <LoadingState label="Checking your access" />
        </SectionCard>
      ) : !operator.allowed ? (
        <NotAnOperator />
      ) : (
        <SectionCard
          flush
          title="Named users"
          description="Suspending here is platform-wide: the person loses access to every organization at once. To remove someone from a single organization, use that organization's Team page."
        >
          {users.isPending ? (
            <LoadingState label="Loading people" />
          ) : users.isError ? (
            <ErrorState
              description={describeFailure(users.error).message}
              requestId={describeFailure(users.error).requestId}
              onRetry={() => void users.refetch()}
            />
          ) : (
            <ResourceTable
              caption="Named users of the platform"
              columns={columns}
              rows={users.data.data.data}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  icon={<UsersRound />}
                  title="No users yet"
                  description="Users appear here once they are invited to an organization."
                />
              }
            />
          )}
        </SectionCard>
      )}

      {confirming !== null ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setStatus.reset();
              setConfirming(null);
            }
          }}
          title={
            confirming.intent === 'suspend'
              ? `Suspend ${confirming.user.display_name} platform-wide?`
              : `Reactivate ${confirming.user.display_name}?`
          }
          description={
            confirming.intent === 'suspend'
              ? 'They lose access to every organization immediately, on their very next request.'
              : 'They regain access to every organization they are still a member of.'
          }
          footer={
            <>
              <Button onClick={() => setConfirming(null)} disabled={setStatus.isPending}>
                Cancel
              </Button>
              <Button
                variant={confirming.intent === 'suspend' ? 'danger' : 'primary'}
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate(
                    { userId: confirming.user.id, intent: confirming.intent },
                    { onSuccess: () => setConfirming(null) },
                  )
                }
              >
                {setStatus.isPending
                  ? 'Working…'
                  : confirming.intent === 'suspend'
                    ? 'Suspend'
                    : 'Reactivate'}
              </Button>
            </>
          }
        >
          {failure ? (
            <div role="alert" className="text-[13px] text-[var(--vc-danger-text)]">
              {failure.message}
              {failure.requestId ? (
                <p className="mt-1 text-[11.5px] text-[var(--vc-text-subtle)]">
                  Request <Mono>{failure.requestId}</Mono>
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-[13px] text-[var(--vc-text-muted)]">
              Their memberships are left untouched — a suspension freezes access, it does not
              dissolve relationships.
            </p>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}

export const platformOrganizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/organizations',
  component: PlatformOrganizationsPage,
});

export const platformUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/users',
  component: PlatformUsersPage,
});
